import { useTranslation } from 'react-i18next';
import { Typography } from '@/components';
import { Step } from '../../my-opportunities/table/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const STEPS: Step[] = [
  { key: 'draft', label: 'Deal Preparation' },
  { key: 'pending', label: 'Under Review' },
  { key: 'published', label: 'Published' },
  { key: 'negotiations', label: 'Negotiations' },
  { key: 'deal_closed', label: 'Deal Closed' },
];

type OpportunityStepperProps = {
  readonly status: string;
  readonly closedType?: string;
};

export function OpportunityStepper({ status, closedType }: OpportunityStepperProps) {
  const { t } = useTranslation(['opportunities']);

  const normalizedStep = (() => {
    if (['draft', 'pending', 'published'].includes(status)) return status;
    if (closedType === 'negotiations') return 'negotiations';
    if (closedType && ['deal_closed', 'deal_cancelled'].includes(closedType)) return 'deal_closed';
    return null;
  })();

  const currentIndex = STEPS.findIndex((step) => step.key === normalizedStep);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Typography as="h3" weight="semibold" size="title" className="uppercase">
            {t('Opportunity Status')}
          </Typography>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-3">
        <div className="overflow-x-auto pb-2">
          <div className="min-w-[560px]">
            <div className="relative mb-3 w-full">
              <div
                className="absolute top-2 z-0 h-1 bg-gray-200"
                style={{
                  left: `calc(10% - 0.625rem)`,
                  width: `calc(80% + 1.25rem)`,
                }}
              />
              <div
                className="absolute top-2 z-0 h-1 bg-green-700 transition-all"
                style={{
                  left: `calc(10% - 0.625rem)`,
                  width: currentIndex > 0 ? `calc(${(currentIndex / (STEPS.length - 1)) * 80}% + 0.625rem)` : '0%',
                }}
              />

              <div className="relative z-0 flex justify-between">
                {STEPS.map((_, index) => {
                  const isActive = index <= currentIndex;
                  return (
                    <div key={index} className="flex w-[20%] justify-center">
                      <div
                        className={`h-5 w-5 rounded-full border-2 ${
                          isActive ? 'border-green-700 bg-green-700' : 'border-gray-300 bg-gray-300'
                        }`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-between text-xs font-medium text-gray-700 sm:text-sm">
              {STEPS.map((step) => (
                <div key={step.key} className="w-[20%]">
                  <Typography as="p" weight="normal" size="body-2" className="text-center">
                    {step.label}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className="mt-2 flex items-center gap-1 text-xs text-gray-400 lg:hidden">{'<-> Scroll to see all steps'}</p>
      </CardContent>
    </Card>
  );
}
